import { FluentProvider, makeStyles } from '@fluentui/react-components';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ApiProvider } from './api/ApiProvider';
import { AuthGate } from './auth/AuthGate';
import { LEGENDS, legendsDarkTheme, legendsLightTheme } from './brand/theme';
import type { AppConfig } from './config';
import { NewRequestPage } from './pages/NewRequestPage';
import { RequestsPage } from './pages/RequestsPage';
import { RequestDetailPage } from './request/RequestDetailPage';
import { AppShell } from './shell/AppShell';
import { CampaignsPage } from './testing/CampaignsPage';
import { TestingPage } from './testing/TestingPage';
import { useThemePreference } from './shell/theme';
import { WizardPage } from './wizard/WizardPage';

const useStyles = makeStyles({
  // Light skin: headings in Legends blue, as in the Legends design deck.
  light: { '& h1:not([data-hero]), & h2': { color: LEGENDS.blue } },
  dark: { '& h1': { color: LEGENDS.navyText } },
});

export function App({ config }: { config: AppConfig }) {
  const styles = useStyles();
  const [theme, toggleTheme] = useThemePreference();

  return (
    <FluentProvider
      theme={theme === 'dark' ? legendsDarkTheme : legendsLightTheme}
      // Height inline: Fluent copies the provider className onto popup portals, and a
      // full-height class there turns every dropdown into a full-screen sheet.
      style={{ height: '100%' }}
      className={theme === 'dark' ? styles.dark : styles.light}
    >
      <AuthGate config={config}>
        <ApiProvider config={config}>
          <BrowserRouter>
            <AppShell theme={theme} onToggleTheme={toggleTheme}>
              <Routes>
                <Route path="/" element={<RequestsPage />} />
                <Route path="/requests/new" element={<NewRequestPage />} />
                <Route path="/requests/:id" element={<RequestDetailPage />} />
                <Route path="/requests/:id/edit/:section" element={<WizardPage />} />
                <Route path="/testing" element={<TestingPage />} />
                <Route path="/testing/campaigns" element={<CampaignsPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AppShell>
          </BrowserRouter>
        </ApiProvider>
      </AuthGate>
    </FluentProvider>
  );
}
